/* ──────────────────────────────────────────────────────────────────────────
 * Architecture coverage — §19, §20, and the map across all 22 points.
 *
 * §19 asks that everything sit under the five InQAI capabilities. §20 gives
 * the layered target architecture. Both are claims about how the platform
 * is organised, and a claim about organisation is worth exactly nothing if
 * nobody can check it.
 *
 * So this file is the claim, and coverage.test.js checks it: every module
 * named here must actually exist and export what is claimed. A section
 * marked `built` whose module is missing fails the build. That turns "we
 * cover all 22 points" from a sentence in a deck into something a reviewer
 * can run.
 *
 * ── STATUS VALUES ──
 *   built        — implemented, tested, and wired into the platform
 *   partial      — implemented but with a stated limitation
 *   architecture — declared and designed, not implementable in a browser
 *   narrative    — a positioning statement, nothing to implement
 *
 * "architecture" is not a euphemism for missing. It is used only where the
 * control genuinely belongs to a deployment layer this demonstration does
 * not have, and each one names that layer.
 * ────────────────────────────────────────────────────────────────────────── */

/* ── §19 The five InQAI capabilities ───────────────────────────────────── */
export const CAPABILITIES = {
  decision: {
    id: "decision",
    label: { en: "Decision Intelligence", mr: "निर्णय बुद्धिमत्ता" },
    covers: ["Prediction", "Simulation", "Risk prioritisation", "Intervention selection", "Resource optimisation"],
    modules: [
      "geo/services/liveModel",
      "geo/services/interventions",
      "geo/services/riskEngine",
      "geo/services/multiHazard",
      "geo/services/emergencyResponse",
    ],
  },
  operational: {
    id: "operational",
    label: { en: "Operational Intelligence", mr: "कार्यात्मक बुद्धिमत्ता" },
    covers: ["Command centre", "Incidents", "Departments", "Services", "Resources", "SLA / status"],
    modules: [
      "spine/incidentMachine",
      "spine/repository",
      "geo/services/serviceStress",
      "geo/services/mobilityChain",
      "missions/missions",
    ],
  },
  spatial: {
    id: "spatial",
    label: { en: "Spatial & Vision Intelligence", mr: "अवकाशीय बुद्धिमत्ता" },
    covers: ["CCTV", "Crowds", "GIS", "Digital Twin", "Routes", "Zones", "Movement"],
    modules: [
      "twin/twin",
      "twin/entities",
      "geo/services/crowdSafety",
      "geo/services/geojson",
      "geo/config/tileProviders",
    ],
    /* Stated rather than glossed: one of the seven things this capability
     * is supposed to cover has no implementation behind it. */
    gap: "CCTV has no video pipeline. Camera entities exist in the twin; no feed is attached.",
  },
  automation: {
    id: "automation",
    label: { en: "Automation Intelligence", mr: "स्वयंचलन बुद्धिमत्ता" },
    covers: ["Workflow", "Escalation", "Task routing", "Notifications", "Approved action orchestration"],
    modules: [
      "spine/incidentMachine",
      "spine/useIncidentWorkflow",
      "intelligence/router",
    ],
    gap: "Notifications have no delivery channel. There is no SMS, radio or push integration.",
  },
  evaluation: {
    id: "evaluation",
    label: { en: "Evaluation & Trust Intelligence", mr: "मूल्यांकन आणि विश्वास" },
    covers: ["Evidence", "Audit", "AI assurance", "Human authority", "Performance evaluation", "Governance"],
    modules: [
      "spine/evidenceEnvelope",
      "assurance/assurance",
      "governance/privacy",
      "missions/proofMetrics",
    ],
    gap: "Audit immutability belongs to a server-side append-only store. A browser log is not an audit record and is labelled as such throughout.",
  },
};

/* ── §20 The layered architecture ──────────────────────────────────────
 * The brief's diagram, with what actually implements each layer here. */
export const LAYERS = [
  {
    id: "physical", label: { en: "Kumbh physical world", mr: "प्रत्यक्ष जग" },
    implementedBy: [], status: "architecture",
    note: "Pilgrims, roads, ghats, vehicles. Represented in the twin; not instrumented.",
  },
  {
    id: "sensing", label: { en: "Sensing layer", mr: "संवेदन स्तर" },
    implementedBy: ["assurance/assurance"], status: "partial",
    note: "Sixteen feeds are registered. One (OSM tiles) is genuinely live; the rest are simulated or absent, and the registry cannot claim LIVE without an integration.",
  },
  {
    id: "foundry", label: { en: "InQAI Data Foundry", mr: "डेटा फाउंड्री" },
    implementedBy: ["geo/data/provenance", "governance/privacy"], status: "partial",
    note: "Provenance, classification, lineage and access policies are implemented. Connectors, event bus and catalog belong to a server-side platform.",
  },
  {
    id: "twin", label: { en: "Kumbh Digital Twin", mr: "डिजिटल ट्विन" },
    implementedBy: ["twin/twin", "twin/entities"], status: "built",
    note: "24 entity types as a typed graph with relationships, agency ownership and live state.",
  },
  {
    id: "router", label: { en: "Intelligence Router", mr: "बुद्धिमत्ता राउटर" },
    implementedBy: ["intelligence/router"], status: "partial",
    note: "Nine paradigms declared, seven with engines attached. CV and quantum route correctly and report why they cannot answer.",
  },
  {
    id: "engines", label: { en: "Five intelligence engines", mr: "पाच इंजिन" },
    implementedBy: ["geo/services/engines"], status: "built",
    note: "The §19 capability grouping above.",
  },
  {
    id: "risk", label: { en: "Risk & prediction", mr: "जोखीम आणि अंदाज" },
    implementedBy: [
      "geo/services/liveModel", "geo/services/multiHazard",
      "geo/services/serviceStress", "geo/services/mobilityChain",
    ],
    status: "built",
    note: "5/15/30/60-minute forecasting, 16 correlated hazard types, 7 services, mobility chain.",
  },
  {
    id: "simulator", label: { en: "Intervention simulator", mr: "हस्तक्षेप सिम्युलेटर" },
    implementedBy: ["geo/services/interventions"], status: "built",
    note: "Eight scenarios A–H with displacement, cost, lag and availability grounded in the twin.",
  },
  {
    id: "authority", label: { en: "Human authority", mr: "मानवी अधिकार" },
    implementedBy: ["spine/incidentMachine"], status: "built",
    note: "Approve / modify / reject. No dispatch transition is reachable without a recorded authorisation.",
  },
  {
    id: "orchestration", label: { en: "Multi-agency orchestration", mr: "बहु-यंत्रणा समन्वय" },
    implementedBy: ["spine/useIncidentWorkflow", "twin/entities"], status: "partial",
    note: "Eight agencies modelled with ownership and assignment. No inter-agency messaging channel exists.",
  },
  {
    id: "field", label: { en: "Field execution", mr: "क्षेत्रीय अंमलबजावणी" },
    implementedBy: ["intelligence/copilot"], status: "partial",
    note: "A read-only officer interface. Nothing is executed by the platform.",
  },
  {
    id: "evidence", label: { en: "Outcome + evidence", mr: "निष्कर्ष आणि पुरावा" },
    implementedBy: ["spine/evidenceEnvelope", "missions/proofMetrics"], status: "built",
    note: "16-field decision provenance and 13 proof metrics, 8 of them measured.",
  },
];

/* ── The 22 points ─────────────────────────────────────────────────────
 * The authoritative map. `modules` are checked to exist by the test suite. */
export const SECTION_COVERAGE = [
  { n: 1, title: "The fundamental Kumbh problem", status: "narrative", modules: [],
    note: "Problem statement. The platform's answer is the closed loop in §2, implemented across the modules below." },

  { n: 2, title: "The intelligence loop", status: "built",
    modules: ["missions/missions", "spine/incidentMachine"],
    note: "All ten stages SENSE→LEARN are executed by the mission runner and recorded per stage. AUTHORIZE halts for a human by default." },

  { n: 3, title: "What we already demonstrate", status: "narrative", modules: [],
    note: "Inventory of the pre-existing platform." },

  { n: 4, title: "Kumbh Digital Twin", status: "built",
    modules: ["twin/twin", "twin/entities"],
    note: "All 24 entity types with location, capacity, live state, relationships and responsible agency. Impact traversal answers what closing an asset affects." },

  { n: 5, title: "Predictive crowd flow", status: "built",
    modules: ["geo/services/liveModel"],
    note: "Deterministic model with diurnal curve, ritual peaks, transport cohorts and capacity-limited egress. 5/15/30/60-minute horizons." },

  { n: 6, title: "Intervention simulator", status: "built",
    modules: ["geo/services/interventions"],
    note: "Eight scenarios A–H. People are conserved, displacement reported, options without a physical asset marked unavailable." },

  { n: 7, title: "Dynamic ingress / egress", status: "built",
    modules: ["geo/services/liveModel"],
    note: "Ratio of inflow to egress capacity per sector; unknown where no exit is mapped, never 'compatible'." },

  { n: 8, title: "Multi-agency incident-to-action", status: "built",
    modules: ["spine/incidentMachine", "spine/useIncidentWorkflow", "spine/repository"],
    note: "Six-state machine with constrained transitions and an event log." },

  { n: 9, title: "Multi-hazard intelligence", status: "built",
    modules: ["geo/services/multiHazard"],
    note: "All 14 named hazard types plus water and egress. 15 named interaction pairs. Four hazards have no feed and report unmonitored rather than zero." },

  { n: 10, title: "Service stress prediction", status: "built",
    modules: ["geo/services/serviceStress"],
    note: "All seven services with lead time to threshold and the arrival rate that produced it." },

  { n: 11, title: "Mobility intelligence", status: "built",
    modules: ["geo/services/mobilityChain"],
    note: "Seven stages, three parallel ingress channels, serial spine. Disruption propagates to a timed downstream crowd effect." },

  { n: 12, title: "Emergency response intelligence", status: "built",
    modules: ["geo/services/emergencyResponse"],
    note: "Nearest APPROPRIATE responder, crowd-degraded ETA, receiving facility with capacity, supporting agencies. Never dispatches." },

  { n: 13, title: "Field officer copilot", status: "built",
    modules: ["intelligence/copilot", "intelligence/sop"],
    note: "Read-only. Refuses commands, refuses to guess, enforces clearance at the point of answering." },

  { n: 14, title: "Citizen / pilgrim intelligence", status: "built",
    modules: ["governance/pilgrimView"],
    note: "Derived from operational data through the enforced redaction filter, not a separately assembled payload." },

  { n: 15, title: "Evaluation & trust intelligence", status: "built",
    modules: ["spine/evidenceEnvelope"],
    note: "All 16 evidence fields, present-and-null where unfillable, with completeness counted." },

  { n: 16, title: "AI stack assurance", status: "built",
    modules: ["assurance/assurance"],
    note: "Ten assurance signals. Latency genuinely measured; false-alert rate reported as unmeasurable with what would be needed." },

  { n: 17, title: "Data and privacy architecture", status: "partial",
    modules: ["governance/privacy"],
    note: "Ten of fifteen controls enforced in code. Encryption at rest, departmental auth, immutable audit, security monitoring and incident response are declared as deployment-layer architecture." },

  { n: 18, title: "InQAI intelligence router", status: "partial",
    modules: ["intelligence/router"],
    note: "All nine paradigms declared and routed. CV and quantum have no engine and say so rather than falling back to a language model." },

  { n: 19, title: "Five InQAI capabilities", status: "built",
    modules: ["architecture/coverage", "geo/services/engines"],
    note: "Every module is assigned to a capability; three capabilities declare a gap." },

  { n: 20, title: "Target architecture", status: "partial",
    modules: ["architecture/coverage"],
    note: "Twelve layers mapped to implementing modules. Sensing, foundry and orchestration are partial; the physical layer is not instrumented." },

  { n: 21, title: "Four mission workflows", status: "built",
    modules: ["missions/missions"],
    note: "All four run end to end from a signal to a measured outcome, halting at AUTHORIZE." },

  { n: 22, title: "How we prove the solution", status: "partial",
    modules: ["missions/proofMetrics"],
    note: "All 13 metrics computed. Eight measurable, five null with the integration or exercise that would make them real." },
];

export function coverageSummary() {
  const by = (s) => SECTION_COVERAGE.filter((x) => x.status === s).length;
  return {
    total: SECTION_COVERAGE.length,
    built: by("built"),
    partial: by("partial"),
    architecture: by("architecture"),
    narrative: by("narrative"),
    /* The honest headline. */
    statement:
      `${by("built")} of ${SECTION_COVERAGE.length} sections are implemented and tested. `
      + `${by("partial")} are partial with the limitation stated. `
      + `${by("narrative")} are positioning statements with nothing to implement. `
      + `No section is claimed complete where a data source does not exist.`,
    capabilityGaps: Object.values(CAPABILITIES)
      .filter((c) => c.gap)
      .map((c) => ({ capability: c.label.en, gap: c.gap })),
  };
}
